// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import {
  RoleDetail,
  RoleUserSelections,
  IAMUser,
  ScopeCategory,
  ScopeConstraint,
  Role,
  Loadable,
  Loaded,
  LoadingStatus,
  RoleGroupSelections
} from '@app/core';
import { RoleDetailsActionsUnion, RoleDetailsActionTypes } from './role-details.actions';
import { RoleGroup } from '../core/models/groups.model';

interface RoleFromList {
  data: {
    role: Role;
  };
}

export interface RoleDetailsState {
  [roleId: string]: {
    details: RoleFromList | Loadable | Loaded<RoleDetail>;
    roleUserSelections?: RoleUserSelections;
    roleGroupSelections?: RoleGroupSelections;
  };
}

export function roleDetailsReducer(
  state: Readonly<RoleDetailsState> = {},
  action: RoleDetailsActionsUnion
): Readonly<RoleDetailsState> {
  switch (action.type) {
    case RoleDetailsActionTypes.SELECT_ROLE:
      return {
        ...state,
        [action.payload.id]: {
          ...(state[action.payload.id] || {}),
          details: {
            ...(state[action.payload.id] || {}).details,
            data: {
              ...(getRoleDetails(action.payload.id)(state) || {}),
              role: action.payload
            }
          }
        }
      };

    case RoleDetailsActionTypes.SELECT_USERS_FOR_ROLE:
      return {
        ...state,
        [action.payload.roleId]: {
          ...state[action.payload.roleId],
          roleUserSelections: {
            ...getRoleUserSelections(action.payload.roleId)(state),
            users: action.payload.selectedUsers
          }
        }
      };

    case RoleDetailsActionTypes.SELECT_SCOPE_CATEGORY:
      return {
        ...state,
        [action.payload.roleId]: {
          ...state[action.payload.roleId],
          roleUserSelections: {
            ...getRoleUserSelections(action.payload.roleId)(state),
            category: action.payload.category,
            constraints: undefined
          }
        }
      };
    case RoleDetailsActionTypes.SELECT_SCOPE_CATEGORY_FOR_GROUP:
      return {
        ...state,
        [action.payload.roleId]: {
          ...state[action.payload.roleId],
          roleGroupSelections: {
            ...getRoleGroupSelections(action.payload.roleId)(state),
            category: action.payload.category,
            constraints: undefined
          }
        }
      };

    case RoleDetailsActionTypes.SELECT_SCOPE_CONSTRAINTS:
      return {
        ...state,
        [action.payload.roleId]: {
          ...state[action.payload.roleId],
          roleUserSelections: {
            ...getRoleUserSelections(action.payload.roleId)(state),
            constraints: action.payload.constraints
          }
        }
      };
    case RoleDetailsActionTypes.SELECT_SCOPE_CONSTRAINTS_FOR_GROUP:
      return {
        ...state,
        [action.payload.roleId]: {
          ...state[action.payload.roleId],
          roleGroupSelections: {
            ...getRoleGroupSelections(action.payload.roleId)(state),
            constraints: action.payload.constraints
          }
        }
      };

    case RoleDetailsActionTypes.ROLE_DETAILS_LOADING:
      return {
        ...state,
        [action.payload]: {
          ...(state[action.payload] || {}),
          details: {
            ...(state[action.payload] || {}).details,
            status: LoadingStatus.LOADING
          }
        }
      };

    case RoleDetailsActionTypes.ROLE_DETAILS_LOAD_COMPLETE:
      return action.payload.role
        ? {
            ...state,
            [action.payload.role.id]: {
              details: {
                data: action.payload,
                status: LoadingStatus.LOAD_COMPLETE
              }
            }
          }
        : state;

    case RoleDetailsActionTypes.ROLE_DETAILS_LOAD_FAILED:
      return {
        ...state,
        [action.payload]: {
          details: {
            ...(state[action.payload] || {}).details,
            status: LoadingStatus.LOAD_FAILED
          }
        }
      };

    case RoleDetailsActionTypes.CLEAR_ROLE_USER_SELECTIONS:
      return {
        ...state,
        [action.payload]: {
          ...(state[action.payload] || {}),
          roleUserSelections: undefined
        }
      };
    case RoleDetailsActionTypes.SELECT_GROUPS_FOR_ROLE:
      return {
        ...state,
        [action.payload.roleId]: {
          ...state[action.payload.roleId],
          roleGroupSelections: {
            ...getRoleGroupSelections(action.payload.roleId)(state),
            groups: action.payload.selectedGroups
          }
        }
      };
    case RoleDetailsActionTypes.CLEAR_ROLE_GROUP_SELECTIONS:
      return {
        ...state,
        [action.payload]: {
          ...(state[action.payload] || {}),
          roleGroupSelections: undefined
        }
      };
    case RoleDetailsActionTypes.REMOVE_GROUP_FROM_ROLE:
      return {
        ...state,
        [action.payload]: {
          ...(state[action.payload] || {}),
          details: {
            ...(state[action.payload].details || {}),
            data: {
              ...(state[action.payload].details.data || {}),
              groups: state[action.payload].details.data.groups.filter((x: any) => x.grant_id !== action.grant_id)
            }
          }
        }
      };

    case RoleDetailsActionTypes.ORG_ROLE_DETAILS_LOADING:
      return {
        ...state,
        [action.payload.roleId]: {
          ...(state[action.payload.roleId] || {}),
          details: {
            ...(state[action.payload.roleId] || {}).details,
            status: LoadingStatus.LOADING
          }
        }
      };

    case RoleDetailsActionTypes.ORG_ROLE_DETAILS_LOAD_COMPLETE:
      return action.payload.role
        ? {
            ...state,
            [action.payload.role.id]: {
              details: {
                data: action.payload,
                status: LoadingStatus.LOAD_COMPLETE
              }
            }
          }
        : state;

    case RoleDetailsActionTypes.ORG_ROLE_DETAILS_LOAD_FAILED:
      return {
        ...state,
        [action.payload.roleId]: {
          details: {
            ...(state[action.payload.roleId] || {}).details,
            status: LoadingStatus.LOAD_FAILED
          }
        }
      };
  }

  return state;
}

export function getRoleDetailsLoadable(roleId: string): (state: RoleDetailsState) => Loadable | undefined {
  return (state: RoleDetailsState) => {
    if (!state[roleId]) {
      return undefined;
    }
    const loadableDetails = <Loadable>state[roleId].details;
    if (!loadableDetails.status) {
      return undefined;
    }
    return loadableDetails;
  };
}

export function getRoleDetails(roleId: string): (state: RoleDetailsState) => RoleDetail | undefined {
  return (state: RoleDetailsState) => {
    if (!state[roleId]) {
      return undefined;
    }
    return state[roleId].details.data;
  };
}

export function getSelectedUsers(roleId: string): (state: RoleDetailsState) => IAMUser[] {
  return (state: RoleDetailsState) => {
    const roleUserSelections = getRoleUserSelections(roleId)(state);
    if (!roleUserSelections || !roleUserSelections.users) {
      return [];
    }
    return roleUserSelections.users;
  };
}

export function getSelectedRoleScopeCategory(roleId: string): (state: RoleDetailsState) => ScopeCategory | undefined {
  return (state: RoleDetailsState) => {
    const roleUserSelections = getRoleUserSelections(roleId)(state);
    if (!roleUserSelections) {
      return undefined;
    }
    return roleUserSelections.category;
  };
}

export function getSelectedRoleScopeCategoryForGroup(
  roleId: string
): (state: RoleDetailsState) => ScopeCategory | undefined {
  return (state: RoleDetailsState) => {
    const roleUserSelections = getRoleGroupSelections(roleId)(state);
    if (!roleUserSelections) {
      return undefined;
    }
    return roleUserSelections.category;
  };
}

export function getSelectedRoleScopeConstraints(roleId: string): (state: RoleDetailsState) => ScopeConstraint[] {
  return (state: RoleDetailsState) => {
    const roleUserSelections = getRoleUserSelections(roleId)(state);
    if (!roleUserSelections || !roleUserSelections.constraints) {
      return [];
    }
    return roleUserSelections.constraints || [];
  };
}

export function getRoleUserSelections(roleId: string): (state: RoleDetailsState) => RoleUserSelections | undefined {
  return (state: RoleDetailsState) => {
    if (!state[roleId]) {
      return undefined;
    }

    return state[roleId].roleUserSelections;
  };
}

export function getUserToEdit(roleId: string): (state: RoleDetailsState) => IAMUser | undefined {
  return (state: RoleDetailsState) => {
    if (state[roleId] === undefined) {
      return undefined;
    }

    const selections = state[roleId].roleUserSelections;
    if (selections === undefined || selections.users === undefined || selections.users.length !== 1) {
      return undefined;
    }
    return selections.users[0];
  };
}

export function getRoleGroupSelections(roleId: string): (state: RoleDetailsState) => RoleGroupSelections | undefined {
  return (state: RoleDetailsState) => {
    if (!state[roleId]) {
      return undefined;
    }

    return state[roleId].roleGroupSelections;
  };
}



// WEBPACK FOOTER //
// ./src/app/access-management/state/role-details.reducer.ts